if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;

        this.slidesPerViewDesktop = parseFloat(this.dataset.slidesPerViewDesktop);
        this.slidesPerViewMobile = parseFloat(this.dataset.slidesPerViewMobile);
      }

      connectedCallback() {
        this.init();

        // Listen for quick view custom elements ready event
        this.quickViewListener = (event) => {
          if (event.detail.container.contains(this)) {
            this.init();
          }
        };
        document.addEventListener('quick-view:custom-elements-ready', this.quickViewListener);
      }

      disconnectedCallback() {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }

        // Remove quick view listener
        if (this.quickViewListener) {
          document.removeEventListener('quick-view:custom-elements-ready', this.quickViewListener);
        }

        // Full inline-zoom teardown so a Theme Editor disconnect/reconnect re-registers cleanly.
        if (this.inlineZoomInitialised) {
          this.resetZoom();
          if (this.boundResizeListener) window.removeEventListener('resize', this.boundResizeListener);
          if (this.boundZoomPointerDown) this.removeEventListener('pointerdown', this.boundZoomPointerDown);
          if (this.boundZoomClick) this.removeEventListener('click', this.boundZoomClick);
          this.inlineZoomInitialised = false;
        }

        if (this.hoverZoomInitialised) {
          this.resetHover();
          if (this.boundHoverPointerMove) this.removeEventListener('pointermove', this.boundHoverPointerMove);
          if (this.boundHoverPointerLeave) this.removeEventListener('pointerleave', this.boundHoverPointerLeave);
          if (this.boundHoverClick) this.removeEventListener('click', this.boundHoverClick);
          if (this.boundHoverReset) {
            window.removeEventListener('resize', this.boundHoverReset);
            window.removeEventListener('scroll', this.boundHoverReset);
          }
          this.hoverZoomInitialised = false;
        }
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.imageZoomMode = this.dataset.imageZoomMode || 'lightbox';
        this.hoverZoomLevel = parseFloat(this.dataset.hoverZoomLevel) || 2;
        this.hoverClickAction = this.dataset.hoverClickAction || 'none';
        this.mobileZoomFallback = this.dataset.mobileZoomFallback || 'inline';
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          if (this.imageZoomMode === 'inline') {
            this.initInlineZoom();
          } else if (this.imageZoomMode === 'hover') {
            this.initHoverZoom();
          } else {
            this.initImageZoom();
          }
        }

        // Add resize listener for vertical carousel
        if (this.mediaLayout === 'vertical-carousel') {
          this.resizeObserver = new ResizeObserver(() => {
            this.updateThumbnailHeight();
          });
          this.resizeObserver.observe(this);
        }
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.slidesPerViewMobile,
          spaceBetween: mediaItemGap,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
          breakpoints: {
        768: {
          slidesPerView: this.slidesPerViewDesktop,
          spaceBetween: mediaItemGap,
        },
      },
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: this.slidesPerViewMobile,
              breakpoints: {
                768: {
                  slidesPerView: this.slidesPerViewDesktop,
                  spaceBetween: mediaItemGap,
                },
              },
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();

          // Update thumbnail height for vertical carousel after slider init
          if (this.mediaLayout === 'vertical-carousel') {
            setTimeout(() => {
              this.updateThumbnailHeight();
            }, 100);
          }
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      initInlineZoom() {
        if (this.inlineZoomInitialised) return;
        this.inlineZoomInitialised = true;

        const firstLevel = parseFloat(this.dataset.zoomLevel1) || 2;
        const secondLevel = parseFloat(this.dataset.zoomLevel2) || 3.5;
        // Sort ascending so a misconfigured level 2 < level 1 still cycles in→in→reset, not in→out.
        this.zoomLevels = [1, firstLevel, secondLevel].sort((a, b) => a - b);
        this.zoomIndex = 0;
        this.activeZoom = null;
        this.zoomPointers = new Map();
        this.pinchStartDistance = 0;
        this.pinchStartScale = 1;

        // Move/up listeners bind to window only while a gesture is active (see onZoomPointerDown),
        // so the gallery never carries a permanent non-passive pointermove that would slow page scrolling.
        // Bind handlers once and reuse the references, so they can be removed on disconnect.
        this.boundZoomPointerMove = this.boundZoomPointerMove || this.onZoomPointerMove.bind(this);
        this.boundZoomPointerUp = this.boundZoomPointerUp || this.onZoomPointerUp.bind(this);
        this.boundZoomPointerDown = this.boundZoomPointerDown || this.onZoomPointerDown.bind(this);
        this.boundZoomClick = this.boundZoomClick || this.onZoomClick.bind(this);

        // A zoomed image's cached rect and offsets go stale on resize/orientation change, so reset.
        this.boundResizeListener = this.boundResizeListener || this.resetZoom.bind(this);
        window.addEventListener('resize', this.boundResizeListener);

        // Activation lives on click so keyboard and assistive tech (which fire a synthetic click,
        // not pointer/key events) work; pointer events only enhance it with drag and pinch.
        this.addEventListener('pointerdown', this.boundZoomPointerDown);
        this.addEventListener('click', this.boundZoomClick);
      }

      removeZoomGestureListeners() {
        window.removeEventListener('pointermove', this.boundZoomPointerMove);
        window.removeEventListener('pointerup', this.boundZoomPointerUp);
        window.removeEventListener('pointercancel', this.boundZoomPointerUp);
      }

      initHoverZoom() {
        if (this.hoverZoomInitialised) return;

        this.hoverMql = this.hoverMql || window.matchMedia('(hover: hover) and (pointer: fine)');

        // Touch / coarse-pointer devices can't hover, so route to the merchant-chosen fallback instead.
        // The buttons render as .js-hover-zoom, so retag them with the class the fallback handler binds to.
        if (!this.hoverMql.matches) {
          this.hoverZoomInitialised = true;
          if (this.mobileZoomFallback === 'inline') {
            this.querySelectorAll('.js-hover-zoom').forEach((button) => {
              button.classList.add('js-inline-zoom');
              button.removeAttribute('aria-haspopup');
              button.setAttribute('aria-pressed', 'false');
            });
            this.initInlineZoom();
          } else if (this.mobileZoomFallback === 'lightbox') {
            this.querySelectorAll('.js-hover-zoom').forEach((button) => button.classList.add('js-photoswipe--zoom'));
            this.initImageZoom();
          } else if (this.mobileZoomFallback === 'off') {
            // No touch zoom: take the overlay button and its decorative icon out of play so they aren't dead controls.
            this.querySelectorAll('.js-hover-zoom').forEach((button) => {
              button.disabled = true;
              button.hidden = true;
              const container = button.closest('.product__media-container');
              const icon = container && container.querySelector('.product__media-icon');
              if (icon) icon.hidden = true;
            });
          }
          return;
        }

        this.hoverZoomInitialised = true;

        // The lightbox backs both the click action and the keyboard/assistive-tech floor, so build it.
        this.initImageZoom();

        this.activeHover = null;
        this.boundHoverPointerMove = this.boundHoverPointerMove || this.onHoverPointerMove.bind(this);
        this.boundHoverPointerLeave = this.boundHoverPointerLeave || this.resetHover.bind(this);
        this.boundHoverClick = this.boundHoverClick || this.onHoverClick.bind(this);

        this.addEventListener('pointermove', this.boundHoverPointerMove);
        this.addEventListener('pointerleave', this.boundHoverPointerLeave);
        this.addEventListener('click', this.boundHoverClick);

        // The rect is cached per hover; drop the zoom if the viewport shifts so it re-measures on the next move.
        this.boundHoverReset = this.boundHoverReset || this.resetHover.bind(this);
        window.addEventListener('resize', this.boundHoverReset);
        window.addEventListener('scroll', this.boundHoverReset, { passive: true });
      }

      getHoverTarget(node) {
        const button = node && node.closest ? node.closest('.js-hover-zoom') : null;
        if (!button || button.dataset.mediaType !== 'image') return null;
        const container = button.closest('.product__media-container');
        const item = button.closest('.product__media-item');
        const img = container && container.querySelector('img');
        if (!container || !img) return null;
        return { button, container, item, img };
      }

      setActiveHover(target) {
        if (this.activeHover && this.activeHover.img === target.img) return this.activeHover;
        this.resetHover();
        const rect = target.container.getBoundingClientRect();
        this.activeHover = Object.assign({ rect, entering: true }, target);
        this.activeHover.container.classList.add('is-hover-zoomed');
        this.loadHighResZoomImage(this.activeHover);
        // Smooth scale-in from centre on entry (CSS transition); tracking sets transition none for crisp follow.
        this.activeHover.img.style.transition = '';
        this.activeHover.img.style.transform = `scale(${this.hoverZoomLevel})`;
        return this.activeHover;
      }

      onHoverPointerMove(event) {
        const target = this.getHoverTarget(event.target);
        if (!target) {
          if (this.activeHover) this.resetHover();
          return;
        }
        const hover = this.setActiveHover(target);
        if (hover.entering) {
          hover.entering = false;
          return; // let the scale-in animate from centre; track from the next move
        }
        const rect = hover.rect;
        if (!rect.width || !rect.height) return;
        const ratioX = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
        const ratioY = Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1);
        const scale = this.hoverZoomLevel;
        // Move the image opposite the cursor; (0.5 - ratio) keeps translate within ±rect*(scale-1)/2 (self-clamping).
        const translateX = (0.5 - ratioX) * rect.width * (scale - 1);
        const translateY = (0.5 - ratioY) * rect.height * (scale - 1);
        hover.img.style.transition = 'none';
        hover.img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
      }

      onHoverClick(event) {
        const target = this.getHoverTarget(event.target);
        if (!target) return;
        const index = Number(target.button.dataset.mediaIndex) || 0;
        // Coordinate-less click = keyboard / assistive tech: always open the lightbox (the floor),
        // whatever the merchant chose for mouse users.
        const isAssistiveTech = event.detail === 0 || (event.clientX === 0 && event.clientY === 0);
        if ((isAssistiveTech || this.hoverClickAction === 'lightbox') && this.lightbox) {
          this.lightbox.loadAndOpen(index);
        }
      }

      resetHover() {
        const hover = this.activeHover;
        if (!hover) return;
        hover.img.style.transition = '';
        hover.img.style.transform = '';
        hover.container.classList.remove('is-hover-zoomed');
        this.activeHover = null;
      }

      getZoomTarget(node) {
        const button = node && node.closest ? node.closest('.js-inline-zoom') : null;
        if (!button || button.dataset.mediaType !== 'image') return null;
        const container = button.closest('.product__media-container');
        const item = button.closest('.product__media-item');
        const img = container && container.querySelector('img');
        if (!container || !img) return null;
        return { button, container, item, img };
      }

      setActiveZoom(target) {
        if (!this.activeZoom || this.activeZoom.img !== target.img) {
          this.resetZoom();
          this.activeZoom = Object.assign(
            { scale: 1, x: 0, y: 0, moved: false, containerRect: target.container.getBoundingClientRect() },
            target
          );
        }
        return this.activeZoom;
      }

      onZoomPointerDown(event) {
        // Primary button / touch contact only — leave right and middle click to the browser (context menu, etc.).
        if (event.button !== 0) return;
        const target = this.getZoomTarget(event.target);
        if (!target) return;

        this.suppressZoomClick = false;
        const zoom = this.setActiveZoom(target);
        zoom.containerRect = zoom.container.getBoundingClientRect();
        this.zoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        // Only the first pointer resets the pan flag — a second finger mustn't clear an in-progress pan.
        if (this.zoomPointers.size === 1) zoom.moved = false;
        zoom.pointerStart = { x: event.clientX, y: event.clientY };
        zoom.translateStart = { x: zoom.x, y: zoom.y };

        if (this.zoomPointers.size === 1) {
          window.addEventListener('pointermove', this.boundZoomPointerMove, { passive: false });
          window.addEventListener('pointerup', this.boundZoomPointerUp);
          window.addEventListener('pointercancel', this.boundZoomPointerUp);
        }

        const isPinch = this.zoomPointers.size === 2;
        if (isPinch) {
          const points = [...this.zoomPointers.values()];
          this.pinchStartDistance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
          this.pinchStartScale = zoom.scale;
        }

        // Only seize the pointer (and block Swiper) once an actual zoom/pan gesture is underway.
        // While unzoomed, a single-pointer drag must still fall through to Swiper for slide swiping.
        if (zoom.scale > 1 || isPinch) {
          // Capture is an optimisation; window listeners still drive the gesture if it can't be set.
          try {
            zoom.button.setPointerCapture(event.pointerId);
          } catch (error) {}
          event.preventDefault();
          this.setZoomCursor('grabbing');
        }
      }

      onZoomPointerMove(event) {
        const zoom = this.activeZoom;
        if (!zoom || !this.zoomPointers.has(event.pointerId)) return;
        this.zoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.zoomPointers.size === 2) {
          event.preventDefault();
          this.handlePinch();
          return;
        }
        if (zoom.scale <= 1) return;

        event.preventDefault();
        const deltaX = event.clientX - zoom.pointerStart.x;
        const deltaY = event.clientY - zoom.pointerStart.y;
        if (Math.hypot(deltaX, deltaY) > 4) zoom.moved = true;
        zoom.x = zoom.translateStart.x + deltaX;
        zoom.y = zoom.translateStart.y + deltaY;
        this.clampZoom();
        this.applyZoom(false);
        this.setZoomCursor('grabbing');
      }

      onZoomPointerUp(event) {
        const zoom = this.activeZoom;
        if (!zoom) return;
        this.zoomPointers.delete(event.pointerId);
        if (zoom.button.hasPointerCapture && zoom.button.hasPointerCapture(event.pointerId)) {
          zoom.button.releasePointerCapture(event.pointerId);
        }
        if (this.zoomPointers.size < 2) this.pinchStartDistance = 0;
        if (this.zoomPointers.size === 1) {
          const [remainingPointer] = this.zoomPointers.values();
          zoom.pointerStart = { x: remainingPointer.x, y: remainingPointer.y };
          zoom.translateStart = { x: zoom.x, y: zoom.y };
        }
        if (this.zoomPointers.size > 0) return;

        this.removeZoomGestureListeners();

        if (event.type === 'pointercancel') {
          this.suppressNextClick();
          this.resetZoom();
          return;
        }

        // A drag/pinch happened: settle here and swallow the click that follows it.
        // A clean tap does nothing here — the click handler activates the zoom (also covering assistive tech).
        if (zoom.moved) {
          this.suppressNextClick();
          if (zoom.scale <= 1.05) {
            this.resetZoom();
          } else {
            this.setZoomCursor('grab');
          }
        }
      }

      suppressNextClick() {
        this.suppressZoomClick = true;
        // Clear after a short delay so the flag can't stick if no click follows the gesture, while still
        // outlasting the click a mobile browser may dispatch a few ms after pointerup.
        setTimeout(() => {
          this.suppressZoomClick = false;
        }, 50);
      }

      onZoomClick(event) {
        if (this.suppressZoomClick) {
          this.suppressZoomClick = false;
          return;
        }
        const target = this.getZoomTarget(event.target);
        if (!target) return;
        const zoom = this.setActiveZoom(target);
        // Keyboard / assistive-tech activation carries no pointer coordinates (detail 0) — zoom to centre.
        let focalX = 0;
        let focalY = 0;
        if (event.detail !== 0 && !(event.clientX === 0 && event.clientY === 0)) {
          const rect = zoom.containerRect || zoom.container.getBoundingClientRect();
          focalX = event.clientX - rect.left - rect.width / 2;
          focalY = event.clientY - rect.top - rect.height / 2;
        }
        this.cycleZoom(focalX, focalY);
      }

      cycleZoom(focalX, focalY) {
        this.zoomIndex = (this.zoomIndex + 1) % this.zoomLevels.length;
        const nextScale = this.zoomLevels[this.zoomIndex];
        if (nextScale === 1) {
          this.resetZoom();
          return;
        }
        this.zoomToScale(nextScale, focalX, focalY);
      }

      handlePinch() {
        const zoom = this.activeZoom;
        const points = [...this.zoomPointers.values()];
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        if (!this.pinchStartDistance) {
          this.pinchStartDistance = distance;
          this.pinchStartScale = zoom.scale;
          return;
        }
        const maxZoom = this.zoomLevels[this.zoomLevels.length - 1];
        const nextScale = Math.min(Math.max(this.pinchStartScale * (distance / this.pinchStartDistance), 1), maxZoom);
        const rect = zoom.containerRect || zoom.container.getBoundingClientRect();
        const focalX = (points[0].x + points[1].x) / 2 - rect.left - rect.width / 2;
        const focalY = (points[0].y + points[1].y) / 2 - rect.top - rect.height / 2;
        zoom.moved = true;
        this.zoomToScale(nextScale, focalX, focalY);
      }

      zoomToScale(nextScale, focalX, focalY) {
        const zoom = this.activeZoom;
        const animate = this.zoomPointers.size === 0;
        const contentX = (focalX - zoom.x) / zoom.scale;
        const contentY = (focalY - zoom.y) / zoom.scale;
        zoom.scale = nextScale;
        zoom.x = focalX - contentX * nextScale;
        zoom.y = focalY - contentY * nextScale;
        this.clampZoom();
        this.applyZoom(animate);
        this.zoomIndex = this.zoomLevels.reduce((closestIndex, level, index) => {
          return Math.abs(level - nextScale) < Math.abs(this.zoomLevels[closestIndex] - nextScale) ? index : closestIndex;
        }, 0);
        this.afterZoomChange();
      }

      clampZoom() {
        const zoom = this.activeZoom;
        const rect = zoom.containerRect || zoom.container.getBoundingClientRect();
        const maxX = (rect.width * (zoom.scale - 1)) / 2;
        const maxY = (rect.height * (zoom.scale - 1)) / 2;
        zoom.x = Math.min(Math.max(zoom.x, -maxX), maxX);
        zoom.y = Math.min(Math.max(zoom.y, -maxY), maxY);
      }

      applyZoom(animate) {
        const zoom = this.activeZoom;
        zoom.img.style.transition = animate ? '' : 'none';
        zoom.img.style.transform = `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`;
      }

      afterZoomChange() {
        const zoom = this.activeZoom;
        const isZoomed = zoom.scale > 1;
        zoom.container.classList.toggle('is-zoomed', isZoomed);
        zoom.button.setAttribute('aria-pressed', isZoomed ? 'true' : 'false');
        this.setZoomCursor(isZoomed ? 'grab' : 'zoom-in');
        if (this.sliderInstance && this.sliderInstance.slider) {
          this.toggleSliderDraggableState(!isZoomed);
        }
        if (isZoomed) this.loadHighResZoomImage(zoom);
      }

      loadHighResZoomImage(zoom) {
        if (zoom.img.dataset.zoomLoaded === 'true') return;
        const highResSrc = zoom.item && zoom.item.dataset.src;
        if (!highResSrc) return;
        zoom.img.dataset.zoomLoaded = 'true';
        zoom.img.removeAttribute('srcset');
        zoom.img.src = highResSrc;
      }

      setZoomCursor(state) {
        if (this.activeZoom) this.activeZoom.button.dataset.zoomCursor = state;
      }

      resetZoom() {
        this.zoomIndex = 0;
        this.pinchStartDistance = 0;
        if (this.zoomPointers) this.zoomPointers.clear();
        if (this.boundZoomPointerMove) this.removeZoomGestureListeners();
        const zoom = this.activeZoom;
        if (!zoom) return;
        zoom.img.style.transition = '';
        zoom.img.style.transform = '';
        zoom.container.classList.remove('is-zoomed');
        zoom.button.setAttribute('aria-pressed', 'false');
        zoom.button.dataset.zoomCursor = 'zoom-in';
        if (this.sliderInstance && this.sliderInstance.slider) {
          this.toggleSliderDraggableState(true);
        }
        this.activeZoom = null;
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }

          // Update thumbnail height for vertical carousel on init
          if (this.mediaLayout === 'vertical-carousel') {
            this.updateThumbnailHeight();
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          if (this.inlineZoomInitialised) this.resetZoom();
          if (this.hoverZoomInitialised) this.resetHover();
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);

            // Update thumbnail height for vertical carousel
            if (this.mediaLayout === 'vertical-carousel') {
              this.updateThumbnailHeight();
            }
          }
        });
      }

      updateThumbnailHeight() {
        if (!this.sliderInstance || !this.sliderInstance.slider) return;

        const { slides, activeIndex } = this.sliderInstance.slider;
        if (!slides[activeIndex]) return;

        // Find the active image in the main viewer
        const activeSlide = slides[activeIndex];
        const activeImage = activeSlide.querySelector('img');

        if (!activeImage) return;

        // Get the computed height of the active image
        const imageHeight = window.getComputedStyle(activeImage).height;
        const heightValue = parseFloat(imageHeight);

        if (heightValue > 0) {
          // Update the CSS custom property for thumbnail height
          const thumbnailContainer = this.querySelector('.product__media-gallery-thumbails');
          if (thumbnailContainer) {
            thumbnailContainer.style.setProperty('--viewer-height', imageHeight);
          }
        } else {
          // Fallback: try to get height from the slide container
          const slideHeight = window.getComputedStyle(activeSlide).height;
          const slideHeightValue = parseFloat(slideHeight);

          if (slideHeightValue > 0) {
            const thumbnailContainer = this.querySelector('.product__media-gallery-thumbails');
            if (thumbnailContainer) {
              thumbnailContainer.style.setProperty('--viewer-height', slideHeight);
            }
          } else {
            // If image is not loaded yet, wait for it to load
            if (activeImage.complete === false) {
              activeImage.addEventListener('load', () => {
                this.updateThumbnailHeight();
              }, { once: true });
            }
          }
        }
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        if (this.inlineZoomInitialised) this.resetZoom();
        if (this.hoverZoomInitialised) this.resetHover();

        if (this.sliderInstance.slider) {
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1);

          // Update thumbnail height for vertical carousel after variant change
          if (this.mediaLayout === 'vertical-carousel') {
            setTimeout(() => {
              this.updateThumbnailHeight();
            }, 100);
          }
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
